import { Shield, AlertCircle, FileText, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const BookingTerms = () => {
  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-red-600/10 flex items-center justify-center">
              <FileText className="text-red-500" size={36} />
            </div>
          </div>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-red-500 mb-6 uppercase">
            Booking Terms & Conditions
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Professional standards and guidelines for all EWS Productions services
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          {/* Important Notice */}
          <div className="bg-zinc-900 rounded-lg p-8 mb-12 border border-red-900/20">
            <div className="flex items-start gap-4">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-1" size={24} />
              <div>
                <h2 className="text-xl font-semibold text-red-500 mb-3">Important Notice</h2>
                <p className="text-gray-300 leading-relaxed">
                  By making a booking with EWS Productions, you agree to comply with these terms and conditions. 
                  Please read them carefully before confirming your reservation. All bookings are subject to availability 
                  and acceptance by EWS Productions.
                </p>
              </div>
            </div>
          </div>

          {/* 1. Booking Process */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold text-white mb-6 flex items-center gap-3">
              <span className="text-red-500">1.</span> Booking Process & Confirmation
            </h2>
            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20 space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="text-red-500 flex-shrink-0 mt-1" size={20} />
                <p className="text-gray-300">
                  All bookings must be made through our contact form or by email to admin@ewsproduction.com
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="text-red-500 flex-shrink-0 mt-1" size={20} />
                <p className="text-gray-300">
                  Bookings are only confirmed upon receipt of deposit payment and written confirmation from EWS Productions
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="text-red-500 flex-shrink-0 mt-1" size={20} />
                <p className="text-gray-300">
                  Provisional bookings will be held for 48 hours pending deposit payment
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="text-red-500 flex-shrink-0 mt-1" size={20} />
                <p className="text-gray-300">
                  You will receive a booking confirmation email with session details, studio address, and arrival instructions
                </p>
              </div>
            </div>
          </section>

          {/* 2. Payment Terms */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold text-white mb-6 flex items-center gap-3">
              <span className="text-red-500">2.</span> Payment Terms
            </h2>
            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-red-500 mb-3">Deposit Requirements</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Studio Hire & Fashion Shoots: 50% non-refundable deposit required</li>
                    <li>• Boudoir Photography Sessions: £100 non-refundable deposit required</li>
                    <li>• Scene Film Production: 50% non-refundable deposit required</li>
                    <li>• Bespoke Set Design Projects: 30% deposit on design approval, 40% before construction begins</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-red-500 mb-3">Balance Payment</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Full balance must be paid 7 days before your scheduled session</li>
                    <li>• For same-week bookings, full payment is required at time of booking</li>
                    <li>• Payment can be made via bank transfer, credit/debit card, or cash on arrival (by prior arrangement)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-red-500 mb-3">Additional Charges</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Overtime charges apply at £100 per hour beyond booked time</li>
                    <li>• Equipment damage or excessive cleaning fees may be charged</li>
                    <li>• Rush bookings (less than 48 hours notice) incur a 25% premium</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* 3. Cancellation & Rescheduling */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold text-white mb-6 flex items-center gap-3">
              <span className="text-red-500">3.</span> Cancellation & Rescheduling Policy
            </h2>
            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-red-500 mb-3">Client Cancellations</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li>• More than 14 days notice: Deposit refunded minus £50 administration fee</li>
                    <li>• 7-14 days notice: 50% of total booking fee retained</li>
                    <li>• Less than 7 days notice: Full payment retained</li>
                    <li>• No-shows: Full payment retained with no refund</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-red-500 mb-3">Rescheduling</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li>• One free reschedule permitted with 7+ days notice</li>
                    <li>• Subsequent reschedules subject to £50 fee</li>
                    <li>• Must be rescheduled within 3 months of original booking date</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-red-500 mb-3">EWS Productions Cancellations</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Full refund provided if we need to cancel for any reason</li>
                    <li>• Alternative dates offered where possible</li>
                    <li>• No liability for consequential losses or expenses</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* 4. Studio Rules & Conduct */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold text-white mb-6 flex items-center gap-3">
              <span className="text-red-500">4.</span> Studio Rules & Professional Conduct
            </h2>
            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-red-500 mb-3">General Rules</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Maximum capacity must be adhered to for safety and insurance purposes</li>
                    <li>• No smoking inside the studio premises</li>
                    <li>• Food and drink permitted in designated areas only</li>
                    <li>• Studio must be left in the same condition as found</li>
                    <li>• All equipment must be handled with care</li>
                    <li>• Guests must arrive and depart within booked time slot</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-red-500 mb-3">Professional Standards</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Respectful, professional behavior required at all times</li>
                    <li>• Disruptive or inappropriate conduct may result in immediate termination without refund</li>
                    <li>• All participants must be 18+ years of age (ID verification required)</li>
                    <li>• Alcohol consumption permitted in moderation; intoxication not tolerated</li>
                    <li>• Illegal substances strictly prohibited</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* 5. Consent & Safety (Alternative Productions) */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold text-white mb-6 flex items-center gap-3">
              <span className="text-red-500">5.</span> Consent, Boundaries & Safety
            </h2>
            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
              <div className="flex items-start gap-4 mb-6">
                <Shield className="text-red-500 flex-shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="text-xl font-semibold text-white mb-3">Mandatory for All Alternative & Intimate Productions</h3>
                  <p className="text-gray-300 mb-4">
                    EWS Productions operates under strict consent and safety protocols for all alternative, intimate, and boudoir sessions.
                  </p>
                </div>
              </div>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-red-500 flex-shrink-0 mt-1" size={20} />
                  <p>Pre-session consultation required to discuss boundaries, limits, and expectations</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-red-500 flex-shrink-0 mt-1" size={20} />
                  <p>Written consent forms must be completed before any session begins</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-red-500 flex-shrink-0 mt-1" size={20} />
                  <p>Safe words and clear communication protocols established</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-red-500 flex-shrink-0 mt-1" size={20} />
                  <p>Sessions may be paused or stopped at any time by any participant</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-red-500 flex-shrink-0 mt-1" size={20} />
                  <p>Trusted companion permitted to attend if requested</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-red-500 flex-shrink-0 mt-1" size={20} />
                  <p>All equipment sanitized and safety-checked before each use</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-red-500 flex-shrink-0 mt-1" size={20} />
                  <p>First aid trained staff available</p>
                </div>
              </div>
            </div>
          </section>

          {/* 6. Photography & Content Rights */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold text-white mb-6 flex items-center gap-3">
              <span className="text-red-500">6.</span> Photography, Filming & Content Rights
            </h2>
            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20 space-y-4 text-gray-300">
              <p>• Clients retain full ownership of all content created during their session</p>
              <p>• EWS Productions will not use, publish, or share any client content without explicit written permission</p>
              <p>• Separate model release forms required for any promotional or portfolio use</p>
              <p>• Clients responsible for ensuring they have rights to any music, props, or materials brought to sessions</p>
              <p>• Recording or photographing without consent of all participants is strictly prohibited</p>
              <p>• Studio security cameras operate in common areas only (not in private shooting spaces)</p>
            </div>
          </section>

          {/* 7. Liability & Insurance */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold text-white mb-6 flex items-center gap-3">
              <span className="text-red-500">7.</span> Liability & Insurance
            </h2>
            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20 space-y-4 text-gray-300">
              <p>• EWS Productions carries full public liability insurance</p>
              <p>• Clients and participants attend at their own risk</p>
              <p>• EWS Productions not liable for personal injury unless caused by our negligence</p>
              <p>• Not responsible for loss or damage to personal belongings</p>
              <p>• Professional productions should carry their own production insurance</p>
              <p>• Clients liable for any damage to studio equipment or property caused by negligence</p>
            </div>
          </section>

          {/* 8. Privacy & Data Protection */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold text-white mb-6 flex items-center gap-3">
              <span className="text-red-500">8.</span> Privacy & Confidentiality
            </h2>
            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20 space-y-4 text-gray-300">
              <p>• All bookings and sessions treated with complete confidentiality</p>
              <p>• Personal data handled in accordance with GDPR regulations</p>
              <p>• Client information never shared with third parties</p>
              <p>• Secure, discreet entry and exit arrangements available</p>
              <p>• NDA agreements available upon request for high-profile clients</p>
            </div>
          </section>

          {/* 9. Bespoke Set Design */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold text-white mb-6 flex items-center gap-3">
              <span className="text-red-500">9.</span> Bespoke Set Design & Construction
            </h2>
            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20 space-y-4 text-gray-300">
              <p>• Design concepts and CAD drawings remain intellectual property of EWS Productions until final payment</p>
              <p>• Construction timeline estimates provided but subject to material availability</p>
              <p>• Changes to approved designs may incur additional costs</p>
              <p>• Client approval required at key construction milestones</p>
              <p>• Storage fees apply if completed sets not collected within agreed timeframe</p>
              <p>• Set dismantling and disposal services available at additional cost</p>
            </div>
          </section>

          {/* 10. Force Majeure */}
          <section className="mb-12">
            <h2 className="text-3xl font-semibold text-white mb-6 flex items-center gap-3">
              <span className="text-red-500">10.</span> Force Majeure
            </h2>
            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20 space-y-4 text-gray-300">
              <p>• Neither party liable for failure to perform obligations due to circumstances beyond reasonable control</p>
              <p>• Includes but not limited to: natural disasters, pandemics, government restrictions, power failures</p>
              <p>• Alternative dates offered where possible or full refund provided</p>
            </div>
          </section>

          {/* Contact */}
          <div className="bg-red-900/10 rounded-lg p-8 border border-red-600/30 text-center">
            <h2 className="text-2xl font-semibold text-white mb-4">Questions About Our Terms?</h2>
            <p className="text-gray-300 mb-6">
              If you have any questions or require clarification on any of these terms, please don't hesitate to contact us.
            </p>
            <Link 
              to="/contact"
              className="inline-block bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
            >
              Contact Us
            </Link>
          </div>

          <div className="mt-12 text-center text-gray-500 text-sm">
            <p>Last Updated: January 2025</p>
            <p className="mt-2">EWS Productions | Wembley, London, United Kingdom</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingTerms;
